/**
 * MedRecFilterContext - Global Med/Rec product filter state
 *
 * Only shows filter when store has both isMed AND isRec enabled.
 * Persists filter selection in sessionStorage so it survives page navigation.
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { tokenManager } from '../services/api.js';

// Filter options
export const FILTER_OPTIONS = {
  ALL: 'all',      // Show all products (no filter)
  MED: 'med',      // Medical products only
  REC: 'rec',      // Recreational products only
};

const MedRecFilterContext = createContext(null);

const STORAGE_KEY = 'medRecFilter';

export const MedRecFilterProvider = ({ children }) => {
  // Get current store and company to check if filter should be shown
  const [store, setStore] = useState(() => tokenManager.getCurrentStore());
  const [company, setCompany] = useState(() => tokenManager.getCurrentCompany());

  // Initialize filter from sessionStorage or default to 'all'
  const [filter, setFilter] = useState(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved && Object.values(FILTER_OPTIONS).includes(saved)) {
      return saved;
    }
    return FILTER_OPTIONS.ALL;
  });

  // Check if filter bar should be shown (store or company has both med AND rec)
  const showFilter = useMemo(() => {
    // For customers: use store-level flags
    if (store) {
      return store.isMed === true && store.isRec === true;
    }
    // For employees (no store): use company-level flags
    if (company) {
      return company.isMed === true && company.isRec === true;
    }
    return false;
  }, [store?.isMed, store?.isRec, company?.isMed, company?.isRec]);

  // Persist filter to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, filter);
  }, [filter]);

  // Listen for store/company changes (when user switches stores or companies)
  useEffect(() => {
    const handleStorageChange = () => {
      const newStore = tokenManager.getCurrentStore();
      const newCompany = tokenManager.getCurrentCompany();
      setStore(newStore);
      setCompany(newCompany);

      // Reset filter if neither store nor company supports both med/rec
      const hasBoth = (newStore?.isMed && newStore?.isRec) || (newCompany?.isMed && newCompany?.isRec);
      if (!hasBoth) {
        setFilter(FILTER_OPTIONS.ALL);
      }
    };

    // Check for store/company changes periodically (localStorage events only fire cross-tab)
    const interval = setInterval(handleStorageChange, 1000);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Get filter values for API queries
  const getFilterValues = useMemo(() => {
    if (!showFilter || filter === FILTER_OPTIONS.ALL) {
      return { isMed: null, isRec: null };
    }
    if (filter === FILTER_OPTIONS.MED) {
      return { isMed: true, isRec: null };
    }
    if (filter === FILTER_OPTIONS.REC) {
      return { isMed: null, isRec: true };
    }
    return { isMed: null, isRec: null };
  }, [filter, showFilter]);

  const value = {
    filter,
    setFilter,
    showFilter,
    filterValues: getFilterValues,
    FILTER_OPTIONS,
  };

  return (
    <MedRecFilterContext.Provider value={value}>
      {children}
    </MedRecFilterContext.Provider>
  );
};

export const useMedRecFilter = () => {
  const context = useContext(MedRecFilterContext);
  if (!context) {
    throw new Error('useMedRecFilter must be used within a MedRecFilterProvider');
  }
  return context;
};

export default MedRecFilterContext;